import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import workspaceAPI from '../services/workspaceAPI';

const WorkspaceContext = createContext({
  loading: true,
  error: '',
  permissions: {},
  stats: null,
  company: null,
  userRole: '',
  workspaceAccess: null,
  refresh: () => {},
});

export const useWorkspace = () => useContext(WorkspaceContext);

const syncUserRole = (userRole) => {
  if (!userRole) return;
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!user || user.role === userRole) return;
    user.role = userRole;
    localStorage.setItem('user', JSON.stringify(user));
    window.dispatchEvent(new Event('workspace-user-updated'));
  } catch {
    /* ignore */
  }
};

export const WorkspaceProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [permissions, setPermissions] = useState({});
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [workspaceAccess, setWorkspaceAccess] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await workspaceAPI.getDashboard();
      const {
        permissions: perms,
        stats: dashboardStats,
        company: co,
        userRole: role,
        workspaceAccess: accessMeta,
      } = res.data;
      setPermissions(perms || {});
      setStats(dashboardStats || null);
      setCompany(co || null);
      setUserRole(role || '');
      setWorkspaceAccess(accessMeta || null);
      syncUserRole(role);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load workspace');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const validTill = workspaceAccess?.validTill;
    if (!validTill) return undefined;
    const expiryTs = new Date(validTill).getTime();
    if (Number.isNaN(expiryTs)) return undefined;

    const logout = () => {
      try {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('companyStaffSession');
      } catch {
        /* ignore */
      }
      if (typeof window !== 'undefined') {
        window.location.assign('/login?reason=workspace-expired');
      }
    };

    const delay = expiryTs - Date.now();
    if (delay <= 0) {
      logout();
      return undefined;
    }

    const timer = window.setTimeout(logout, delay);
    return () => window.clearTimeout(timer);
  }, [workspaceAccess?.validTill]);

  return (
    <WorkspaceContext.Provider
      value={{
        loading,
        error,
        permissions,
        stats,
        company,
        userRole,
        workspaceAccess,
        refresh: load,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;
